/**
 * The pause's write, with its refusal shown only while it
 * still matches the panel on screen. A mutation's error otherwise lasts
 * until the next attempt, so a Refresh, a window-focus refetch or another
 * write's answer would leave "try again" beside a panel that has since
 * moved on (a teammate paused, the outage passed). The refusal is dropped
 * once the project's panel query holds data newer than it did when the
 * write failed; a failed refetch changes no data, so it keeps the refusal.
 */

import { useCallback, useState, useSyncExternalStore } from 'react';
import { useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { projectPanelKeys } from './useProjectPanel';
import type { WriteContext } from './panelWrites';
import type { PanelResponse } from '../types/projectPanel.types';

type PanelWrite = UseMutationResult<PanelResponse, Error, boolean, WriteContext>;

export interface PanelSwitch {
  send: (value: boolean) => void;
  isPending: boolean;
  /** The last write's refusal, while no newer panel has arrived. */
  error: Error | null;
}

/** When the project's panel data last changed (0 before any). */
function usePanelUpdatedAt(projectId: string): number {
  const client = useQueryClient();
  const cache = client.getQueryCache();
  const subscribe = useCallback((onChange: () => void) => cache.subscribe(onChange), [cache]);
  return useSyncExternalStore(
    subscribe,
    () => client.getQueryState(projectPanelKeys.panel(projectId))?.dataUpdatedAt ?? 0,
  );
}

export function usePanelSwitch(projectId: string, write: PanelWrite): PanelSwitch {
  const client = useQueryClient();
  const updatedAt = usePanelUpdatedAt(projectId);
  // The panel's data time when the last write failed.
  const [failedAgainst, setFailedAgainst] = useState<number | null>(null);

  const send = (value: boolean): void => {
    write.mutate(value, {
      onError: () => {
        const state = client.getQueryState(projectPanelKeys.panel(projectId));
        setFailedAgainst(state?.dataUpdatedAt ?? 0);
      },
    });
  };
  const current = failedAgainst !== null && updatedAt <= failedAgainst;
  return { send, isPending: write.isPending, error: current ? write.error : null };
}
